import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthCache, ConfigChoice, ConfigMeasurementUnit, FieldOption } from '@mri/types';
import { environment } from '@mri/environment';
import { forkJoin, map, Observable, of, Subject, tap } from 'rxjs';
import { ChoiceTypes } from '@mri/enums';
import { AuthResourceService } from '../auth-resource';
import { getStartToday } from '@mri/functions';


export interface CalcVars {
  startToday: Date;
  speedUnit?: ConfigMeasurementUnit;
  distanceUnit?: ConfigMeasurementUnit;
  diffHours?: number;
}


@Injectable({
  providedIn: 'root'
})
export class ConfigResourceService {

  private _choices!: Array<ConfigChoice>;
  private _units!: Array<ConfigMeasurementUnit>;
  private _options: Array<FieldOption & { fieldKey: string }> = [];
  private _unitsOnTheWay: boolean = false;
  private _choicesOnTheWay: boolean = false;
  private _units$ = new Subject<Array<ConfigMeasurementUnit>>();
  private _choices$ = new Subject<Array<ConfigChoice>>();

  get calcVars(): CalcVars | null {
    const auth = this._authSrc.auth;
    if(!auth) return null;
    return this._generateCalcVars(auth, this._units);
  }

  get choices$(): Observable<Array<ConfigChoice>> {
    if (Array.isArray(this._choices)) return of(this._choices)
    if(!this._choicesOnTheWay) return this.getChoices$();

    return this._choices$;
  }

  get units$(): Observable<Array<ConfigMeasurementUnit>> {
    if (Array.isArray(this._units)) return of(this._units)
    if(!this._unitsOnTheWay) return this.getUnits$();

    return this._units$;
  }

  constructor(
    private _http: HttpClient,
    private _authSrc: AuthResourceService
  ) {
  }

  getChoices$(): Observable<Array<ConfigChoice>> {
    this._choicesOnTheWay = true;
    return this._http.get<Array<ConfigChoice>>(environment.apiUrl + '/config/choices')
      .pipe(
        tap(choices => this._choices = choices),
        tap(choices => this._choices$.next(choices)),
        tap(() => this._fillOptionsChoices())
      );
  }

  getUnits$() {
    this._unitsOnTheWay = true;
    return this._http.get<Array<ConfigMeasurementUnit>>(environment.apiUrl + '/config/units')
      .pipe(
        tap(units => this._units = units),
        tap(units => this._units$.next(units)),
        tap( () => this._fillOptionsUnits())
      );
  }

  bringOptions$() {
    return forkJoin([this.choices$, this.units$]);
  }

  getOptions$(fieldKey: string): Observable<Array<FieldOption>> {
    return this.bringOptions$()
      .pipe(
        map(() => this.getOptions(fieldKey))
      );
  }

  getOptions(fieldKey: string | ChoiceTypes): Array<FieldOption> {
    if (this._options.length === 0) return [];

    return this._options.filter(o => o.fieldKey === fieldKey);
  }

  getChoices(fieldKey: string | ChoiceTypes): Array<ConfigChoice> {
    if (this._options.length === 0) return [];

    return this._choices.filter(o => o.fieldKey === fieldKey);
  }

  getCalcVars(): Observable<CalcVars | null> {
    return forkJoin([this._authSrc.auth$, this.units$])
      .pipe(
        map(([auth, units]) => this._generateCalcVars(auth, units))
      )
  }


  private _generateCalcVars(auth: AuthCache | undefined, units: Array<ConfigMeasurementUnit>): CalcVars | null {
    if(!auth) return null;

    const { property } = auth;
    const { timezone, distanceUnit: duKey, speedUnit: suKeyy } = property?.config || {}

    const diffHours = - (1000 * 60 * 60 * parseInt(timezone ?? '-05:00'));

    const startToday = new Date(getStartToday());

    const distanceUnit: ConfigMeasurementUnit | undefined =
      units.find(u => u.key === duKey) ??
      units.find(u => u.type === ChoiceTypes.DISTANCE );

    const speedUnit: ConfigMeasurementUnit | undefined =
      units.find(u => u.key === suKeyy) ??
      units.find(u => u.type === ChoiceTypes.SPEED );

    return {
      startToday, distanceUnit, speedUnit, diffHours
    }
  }

  private _fillOptionsUnits() {
    this._options = [
      ...this._options,
      ...this._units.map(u => ({
        label: `${u.label['es'] || u.label['default']} (${u.abbr})`,
        value: u.key,
        fieldKey: u.type
      }))
    ];
  }

  private _fillOptionsChoices() {
    this._options = [
      ...this._options,
      ...this._choices.map(c => ({
        label: c.label['es'] || c.label['default'],
        value: c.value,
        image: c.image,
        fieldKey: c.fieldKey
      }))
    ];
  }


}
